import type { EventTypeView, PopularAccumulatorsConfig } from '@ps/isp-web-services-types';
import { FeatureFlag } from '@ps/web-core-enums';
import { type RootState, select } from '@ps/web-core-store';
import { createSelector } from '@rematch/select';
import { uniqBy } from 'lodash';
import type { Selector } from 'react-redux';

import { withMeetingAndEventBasedMarketTemplateModelSelectors } from '@swf/sports/models/market-templates/meetingAndEventBased';
import { notEmpty } from '@swf/sports/utils/notEmpty';

import { defaultState } from './defaultState';
import type { SportsHomePageState } from './types';

export const selectors = withMeetingAndEventBasedMarketTemplateModelSelectors(
  defaultState,
  (slice) => ({
    hasPreloaded: () => slice((state) => state.hasPreloaded),
    isLoading: () => slice((state) => state.isLoading),
    hasErrored: () => slice((state) => state.error),
    popularBetBuilders: () => slice((state) => state.popularBetBuilders),
    hasPopularBetBuilders(this: {
      popularBetBuilders: Selector<RootState, SportsHomePageState['popularBetBuilders']>;
    }) {
      return createSelector(
        this.popularBetBuilders,
        (rootState: RootState) =>
          select.feature.check(rootState, FeatureFlag.ISPEnableHomePagePopularBetBuilders),
        (ppb, isPopularBetBuildersEnabled): boolean =>
          Boolean(isPopularBetBuildersEnabled && ppb.length)
      );
    },
    popularAccumulators: () => slice((state) => state.popularAccumulators),
    filteredPopularAccumulators(this: {
      popularAccumulators: Selector<RootState, SportsHomePageState['popularAccumulators']>;
      popularAccumulatorsSelectedEventType: Selector<
        RootState,
        SportsHomePageState['popularAccumulatorsSelectedEventType']
      >;
      markets: Selector<RootState, SportsHomePageState['markets']>;
    }) {
      return createSelector(
        this.popularAccumulators,
        this.popularAccumulatorsSelectedEventType,
        this.markets,
        (paccas, selectedEventType, markets): PopularAccumulatorsConfig[] =>
          paccas.filter(
            (pacca) => markets[pacca.runnerIds[0].marketId].eventTypeId === selectedEventType
          )
      );
    },
    hasPopularAccumulators: () => slice((state) => Boolean(state.popularAccumulators.length)),
    popularAccumulatorEventTypes(this: {
      popularAccumulators: Selector<RootState, SportsHomePageState['popularAccumulators']>;
      eventTypes: Selector<RootState, SportsHomePageState['eventTypes']>;
      markets: Selector<RootState, SportsHomePageState['markets']>;
    }) {
      return createSelector(
        this.popularAccumulators,
        this.eventTypes,
        this.markets,
        (paccas, eventTypes, markets): EventTypeView[] =>
          uniqBy(
            paccas
              .map((pacca) => {
                const market = markets[pacca.runnerIds[0].marketId];
                return eventTypes[market.eventTypeId];
              })
              .filter(notEmpty),
            (eventType) => eventType.eventTypeId
          )
      );
    },
    popularAccumulatorsSelectedEventType: () =>
      slice((state) => state.popularAccumulatorsSelectedEventType)
  })
);
